import type { SceneDef } from "../../genesis/schema";

/**
 * Le Parcours du Disciple — La Communion et le Service (jeu 1, scène 4).
 * Rendered entirely from data by the WorldManager (Genesis engine).
 *
 * Signature mechanics (Ac 2:42), le parvis d'église au soir doré et le village :
 *  - Un parvis à colonnes ouvert sur le village, lampes du soir allumées,
 *    villageois qui convergent pour le repas du Seigneur.
 *  - La Table de la communion : rompre le pain ensemble — 6 pains nommés
 *    (pickup) + la Coupe (drag_drop) à apporter à la table (Ac 2:42, 46).
 *  - 3 micro-services RÉELS au village, chacun avec sa station :
 *      1. l'eau de la veuve — 3 cruches d'eau fraîche (drag_drop, Jc 1:27) ;
 *      2. le toit du voisin — 3 planches de cèdre (drag_drop) ;
 *      3. les provisions du malade — 3 paniers (drag_drop, Mt 25:36).
 *  - La Cloche du rassemblement et le Foyer de la prière commune
 *    (lever_action) — les quatre persévérances d'Actes 2:42 sont toutes là :
 *    enseignement (panneaux), communion (table), fraction du pain, prières.
 *  - PNJ : Dorcas riche en bonnes œuvres (Ac 9:36), Barnabas le fils de
 *    l'encouragement (Ac 4:36-37).
 *
 * Ancres bibliques : Ac 2:42-47 (la vie de la première communauté),
 * Ga 5:13 (serviteurs les uns des autres par amour), Jc 1:27 (la veuve),
 * Mt 25:36 (visiter le malade), Ac 9:36 (Dorcas), Ac 4:36-37 (Barnabas).
 * Cadrage d'espérance : servir n'est pas une corvée mais une fête du soir —
 * lampes chaudes, table dressée, village en paix. Le Père n'est jamais figuré.
 */
export const parcoursDuDiscipleScene4: SceneDef = {
  id: "parcours-du-disciple/scene-4",
  game: "parcours-du-disciple",
  title: { fr: "Le Parcours du Disciple — La Communion et le Service", en: "The Disciple's Journey — Communion and Service" },
  order: 4,
  keyName: { fr: "Clé de la Porte Étroite", en: "Key of the Narrow Gate" },
  verseBrand: "Matthieu 16:19",
  env: {
    groundSize: 60,
    skyColor: "#e8c088",
    groundColor: "#8a7a55",
    lighting: "dusk",
    props: [
      // Colonnes du parvis qui entourent la table de la communion.
      { id: "colonnes_parvis", shape: "cylinder", pos: [0, 0, -10], color: "#cfc4a8", size: 2, scatter: 8, scatterRadius: 9 },
      // Le portique de l'église, au fond du parvis.
      { id: "portique_eglise", shape: "gate", pos: [0, 0, -24], color: "#b8a878", size: 3.5 },
      // Maisons du village, versant ouest — la veuve habite par là.
      { id: "maisons_ouest", shape: "box", pos: [-22, 0, 6], color: "#a89272", size: 2.4, scatter: 7, scatterRadius: 8 },
      // Maisons du village, versant est — le voisin au toit abîmé et le malade.
      { id: "maisons_est", shape: "box", pos: [22, 0, 6], color: "#a89272", size: 2.4, scatter: 7, scatterRadius: 8 },
      // Lampes du soir suspendues au-dessus du parvis (y > 0).
      { id: "lampes_soir", shape: "flame", pos: [0, 2.2, -4], color: "#ffcf8a", size: 0.3, scatter: 14, scatterRadius: 12 },
      // Oliviers du chemin qui monte au parvis.
      { id: "oliviers_chemin", shape: "tree", pos: [-12, 0, 20], color: "#6d7d46", size: 1.5, scatter: 10, scatterRadius: 9 },
      // Le puits du village, sur la placette ouest.
      { id: "puits_village", shape: "cylinder", pos: [-18, 0, 16], color: "#8f8878", size: 1.4 },
      // Villageois du soir qui convergent vers la table.
      { id: "villageois_soir", shape: "person", pos: [6, 0, 8], color: "#b9a274", size: 1, scatter: 16, scatterRadius: 11 },
      // Fleurs du parvis, plantées le long du portique.
      { id: "fleurs_parvis", shape: "sphere", pos: [-6, 0, -20], color: "#e0a8b8", size: 0.26, scatter: 16, scatterRadius: 7 },
      // Collines dorées du soir à l'horizon nord.
      { id: "collines_soir", shape: "mountain", pos: [0, 0, -28], color: "#7a6f58", size: 3, scatter: 5, scatterRadius: 10 },
      // Étals du petit marché, rangés pour la nuit.
      { id: "etals_marche", shape: "box", pos: [18, 0, 20], color: "#9a7a52", size: 1.2, scatter: 6, scatterRadius: 5 },
    ],
  },
  spawn: [0, 0, 18],
  difficulty: {
    // goalCount scale sur le VRAI total d'interactables avançants :
    // 6 pickup (pains) + 10 drag_drop (coupe + 3 cruches + 3 planches
    // + 3 paniers) + 2 lever_action = 18.
    D: { goalCount: 6, hints: true },
    A: { goalCount: 12, hints: true },
    E: { goalCount: 18, hints: false, timerS: 480 },
  },
  interactables: [
    // ——— La Table de la communion (Ac 2:42) au centre du parvis ———
    {
      id: "table_communion", type: "build_slot",
      label: { fr: "La Table de la communion (Actes 2:42)", en: "The Table of communion (Acts 2:42)" },
      pos: [0, 0, -8], shape: "box", color: "#a5713f", size: 2.2, targetId: "table_communion",
    },
    // ——— 6 pains nommés à rompre ensemble ———
    {
      id: "pain_accueil", type: "pickup",
      label: { fr: "Pain de l'accueil", en: "Bread of welcome" },
      pos: [-5, 0, -4], shape: "box", color: "#d8b26a", size: 0.5, targetId: "table_communion", count: 1,
    },
    {
      id: "pain_pardon", type: "pickup",
      label: { fr: "Pain du pardon", en: "Bread of forgiveness" },
      pos: [5, 0, -3], shape: "box", color: "#d0a85e", size: 0.5, targetId: "table_communion", count: 1,
    },
    {
      id: "pain_joie", type: "pickup",
      label: { fr: "Pain de la joie", en: "Bread of joy" },
      pos: [-8, 0, -10], shape: "box", color: "#d8b26a", size: 0.5, targetId: "table_communion", count: 1,
    },
    {
      id: "pain_esperance", type: "pickup",
      label: { fr: "Pain de l'espérance", en: "Bread of hope" },
      pos: [8, 0, -9], shape: "box", color: "#d0a85e", size: 0.5, targetId: "table_communion", count: 1,
    },
    {
      id: "pain_gratitude", type: "pickup",
      label: { fr: "Pain de la gratitude", en: "Bread of gratitude" },
      pos: [-3, 0, -14], shape: "box", color: "#d8b26a", size: 0.5, targetId: "table_communion", count: 1,
    },
    {
      id: "pain_unite", type: "pickup",
      label: { fr: "Pain de l'unité", en: "Bread of unity" },
      pos: [4, 0, -13], shape: "box", color: "#d0a85e", size: 0.5, targetId: "table_communion", count: 1,
    },
    // ——— La Coupe de la communion ———
    {
      id: "coupe_communion", type: "drag_drop",
      label: { fr: "La Coupe de la communion", en: "The Cup of communion" },
      pos: [0, 0, -3], shape: "cylinder", color: "#8a5a3a", size: 0.55, targetId: "table_communion", count: 1,
    },
    // ——— Micro-service 1 : l'eau de la veuve (Jc 1:27) ———
    {
      id: "maison_veuve", type: "build_slot",
      label: { fr: "La maison de la veuve (Jacques 1:27)", en: "The widow's house (James 1:27)" },
      pos: [-20, 0, 4], shape: "box", color: "#9a8a6a", size: 1.8, targetId: "maison_veuve",
    },
    {
      id: "cruche_eau_1", type: "drag_drop",
      label: { fr: "Cruche d'eau fraîche du puits", en: "Jug of fresh water from the well" },
      pos: [-14, 0, 8], shape: "cylinder", color: "#6f95b5", size: 0.55, targetId: "maison_veuve", count: 1,
    },
    {
      id: "cruche_eau_2", type: "drag_drop",
      label: { fr: "Deuxième cruche d'eau fraîche", en: "Second jug of fresh water" },
      pos: [-16, 0, 0], shape: "cylinder", color: "#6f95b5", size: 0.55, targetId: "maison_veuve", count: 1,
    },
    {
      id: "cruche_eau_3", type: "drag_drop",
      label: { fr: "Troisième cruche d'eau fraîche", en: "Third jug of fresh water" },
      pos: [-24, 0, 10], shape: "cylinder", color: "#6f95b5", size: 0.55, targetId: "maison_veuve", count: 1,
    },
    // ——— Micro-service 2 : le toit du voisin ———
    {
      id: "toit_voisin", type: "build_slot",
      label: { fr: "Le toit du voisin à réparer", en: "The neighbor's roof to mend" },
      pos: [20, 0, 2], shape: "box", color: "#8a7452", size: 1.8, targetId: "toit_voisin",
    },
    {
      id: "planche_cedre_1", type: "drag_drop",
      label: { fr: "Planche de cèdre", en: "Cedar plank" },
      pos: [14, 0, 6], shape: "box", color: "#b08d57", size: 0.6, targetId: "toit_voisin", count: 1,
    },
    {
      id: "planche_cedre_2", type: "drag_drop",
      label: { fr: "Deuxième planche de cèdre", en: "Second cedar plank" },
      pos: [16, 0, -2], shape: "box", color: "#b08d57", size: 0.6, targetId: "toit_voisin", count: 1,
    },
    {
      id: "planche_cedre_3", type: "drag_drop",
      label: { fr: "Troisième planche de cèdre", en: "Third cedar plank" },
      pos: [24, 0, 8], shape: "box", color: "#b08d57", size: 0.6, targetId: "toit_voisin", count: 1,
    },
    // ——— Micro-service 3 : les provisions du malade (Mt 25:36) ———
    {
      id: "maison_malade", type: "build_slot",
      label: { fr: "La maison du malade à visiter (Matthieu 25:36)", en: "The sick man's house to visit (Matthew 25:36)" },
      pos: [12, 0, 16], shape: "box", color: "#9a8a6a", size: 1.8, targetId: "maison_malade",
    },
    {
      id: "panier_provisions_1", type: "drag_drop",
      label: { fr: "Panier de provisions", en: "Basket of provisions" },
      pos: [7, 0, 12], shape: "sphere", color: "#c9945a", size: 0.5, targetId: "maison_malade", count: 1,
    },
    {
      id: "panier_provisions_2", type: "drag_drop",
      label: { fr: "Deuxième panier de provisions", en: "Second basket of provisions" },
      pos: [16, 0, 12], shape: "sphere", color: "#c9945a", size: 0.5, targetId: "maison_malade", count: 1,
    },
    {
      id: "panier_provisions_3", type: "drag_drop",
      label: { fr: "Troisième panier de provisions", en: "Third basket of provisions" },
      pos: [10, 0, 22], shape: "sphere", color: "#c9945a", size: 0.5, targetId: "maison_malade", count: 1,
    },
    // ——— La Cloche du rassemblement ———
    {
      id: "cloche_rassemblement", type: "lever_action",
      label: { fr: "La Cloche du rassemblement", en: "The Bell of gathering" },
      pos: [0, 0, -18], shape: "cone", color: "#c9a24b", size: 1.3, holdS: 2,
    },
    // ——— Le Foyer de la prière commune (Ac 2:42) ———
    {
      id: "foyer_priere", type: "lever_action",
      label: { fr: "Le Foyer de la prière commune (Actes 2:42)", en: "The Hearth of common prayer (Acts 2:42)" },
      pos: [-12, 0, -16], shape: "flame", color: "#f4b45c", size: 1.4, holdS: 3,
    },
    // ——— PNJ nommés ———
    {
      id: "dorcas", type: "npc_dialog",
      label: { fr: "Dorcas, riche en bonnes œuvres (Actes 9:36)", en: "Dorcas, full of good works (Acts 9:36)" },
      pos: [-10, 0, 12], shape: "person", color: "#a08a6b", size: 1.1,
    },
    {
      id: "barnabas", type: "npc_dialog",
      label: { fr: "Barnabas, le fils de l'encouragement (Actes 4:36-37)", en: "Barnabas, the son of encouragement (Acts 4:36-37)" },
      pos: [10, 0, 5], shape: "person", color: "#7a9a6b", size: 1.1,
    },
    // ——— Panneaux bibliques ———
    {
      id: "panel_ac242", type: "panel",
      label: { fr: "Les quatre persévérances", en: "The four steadfast practices" },
      pos: [-4, 0, 3], shape: "stele", color: "#7fa0b5", size: 1.3, panelId: "p_ac242_perseverance",
    },
    {
      id: "panel_ga513", type: "panel",
      label: { fr: "Serviteurs les uns des autres", en: "Servants of one another" },
      pos: [-16, 0, -8], shape: "stele", color: "#a0b57f", size: 1.3, panelId: "p_ga513_service",
    },
  ],
  objectives: [
    {
      id: "obj_table_communion", kind: "build",
      label: { fr: "Dresse la Table de la communion : les 6 pains nommés et la Coupe (Actes 2:42)", en: "Lay the Table of communion: the 6 named loaves and the Cup (Acts 2:42)" },
      target: "table_communion", count: 7,
    },
    {
      id: "obj_micro_services", kind: "build",
      label: { fr: "Accomplis les 3 micro-services du village : l'eau de la veuve, le toit du voisin, les provisions du malade", en: "Complete the 3 micro-services in the village: the widow's water, the neighbor's roof, the sick man's provisions" },
      count: 9,
    },
  ],
  panels: [
    {
      id: "p_ac242_perseverance",
      title: { fr: "Les quatre persévérances", en: "The four steadfast practices" },
      reference: "Actes 2:42",
      passage: {
        fr: "Ils persévéraient dans l'enseignement des apôtres, dans la communion fraternelle, dans la fraction du pain, et dans les prières.",
        en: "They continued steadfastly in the apostles' teaching and fellowship, in the breaking of bread, and prayer.",
      },
      explain: {
        fr: "La première communauté tient debout sur quatre piliers : l'enseignement, la communion fraternelle, la fraction du pain et les prières. Ce parvis les rassemble tous : les panneaux enseignent, la table réunit, le pain se rompt, le foyer prie. Le disciple ne persévère pas seul — il persévère AVEC, soir après soir.",
        en: "The first community stands on four pillars: the teaching, the fellowship, the breaking of bread, and the prayers. This courtyard gathers them all: the panels teach, the table unites, the bread is broken, the hearth prays. The disciple does not persevere alone — he perseveres WITH others, evening after evening.",
      },
      didYouKnow: {
        fr: "Actes 2:46-47 ajoute qu'ils rompaient le pain « avec joie et simplicité de cœur, louant Dieu » — et que le Seigneur ajoutait chaque jour à l'Église ceux qui étaient sauvés.",
        en: "Acts 2:46-47 adds that they broke bread \"with gladness and singleness of heart, praising God\" — and that the Lord added to the assembly day by day those who were being saved.",
      },
      application: {
        fr: "Lequel des quatre piliers est le plus fragile dans ta semaine : l'écoute de la Parole, la communion, le repas partagé ou la prière ? Choisis-en un à fortifier dès ce soir.",
        en: "Which of the four pillars is weakest in your week: listening to the Word, fellowship, the shared meal, or prayer? Choose one to strengthen starting tonight.",
      },
    },
    {
      id: "p_ga513_service",
      title: { fr: "Serviteurs les uns des autres", en: "Servants of one another" },
      reference: "Galates 5:13",
      passage: {
        fr: "Frères, vous avez été appelés à la liberté, seulement ne faites pas de cette liberté un prétexte de vivre selon la chair; mais rendez-vous, par la charité, serviteurs les uns des autres.",
        en: "For you, brothers, were called for freedom. Only don't use your freedom for gain to the flesh, but through love be servants to one another.",
      },
      explain: {
        fr: "La liberté du disciple n'est pas une liberté POUR soi mais une liberté POUR aimer. Les trois micro-services de ce village — l'eau portée à la veuve, le toit du voisin réparé, les provisions déposées chez le malade — sont petits, concrets, presque invisibles : c'est exactement là que la communion devient réelle. Servir par amour n'appauvrit jamais ; c'est la joie du soir.",
        en: "The disciple's freedom is not freedom FOR self but freedom FOR love. The three micro-services of this village — water carried to the widow, the neighbor's roof mended, provisions left at the sick man's door — are small, concrete, almost invisible: that is exactly where communion becomes real. Serving through love never impoverishes; it is the evening's joy.",
      },
      didYouKnow: {
        fr: "Dorcas de Jaffa était « riche en bonnes œuvres et en aumônes » (Actes 9:36) : quand elle mourut, les veuves montrèrent en pleurant les tuniques qu'elle avait cousues — un service discret devenu mémoire de toute une ville.",
        en: "Dorcas of Joppa was \"full of good works and acts of mercy\" (Acts 9:36): when she died, the widows wept and showed the coats she had sewn — a quiet service that became the memory of a whole town.",
      },
      application: {
        fr: "Quel « micro-service » de moins d'une heure pourrais-tu rendre cette semaine à quelqu'un de ton entourage, sans rien attendre en retour ?",
        en: "What \"micro-service\" of less than an hour could you offer someone around you this week, expecting nothing in return?",
      },
    },
  ],
  quizzes: [
    {
      id: "q_perseverances", level: "D",
      prompt: { fr: "Selon Actes 2:42, dans quoi les premiers disciples persévéraient-ils ?", en: "According to Acts 2:42, in what did the first disciples continue steadfastly?" },
      choices: [
        { fr: "L'enseignement, la communion, la fraction du pain et les prières", en: "The teaching, the fellowship, the breaking of bread and the prayers" },
        { fr: "Les longs voyages et les grands discours", en: "Long journeys and great speeches" },
        { fr: "La construction de nouveaux bâtiments", en: "The building of new buildings" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Ils persévéraient dans l'enseignement des apôtres, dans la communion fraternelle, dans la fraction du pain, et dans les prières » (Actes 2:42). Quatre piliers simples qui tiennent encore l'Église aujourd'hui.",
        en: "\"They continued steadfastly in the apostles' teaching and fellowship, in the breaking of bread, and prayer\" (Acts 2:42). Four simple pillars that still hold the Church today.",
      },
    },
    {
      id: "q_dorcas", level: "A",
      prompt: { fr: "Pour quoi Dorcas (Tabitha) était-elle connue à Jaffa (Actes 9:36) ?", en: "What was Dorcas (Tabitha) known for in Joppa (Acts 9:36)?" },
      choices: [
        { fr: "Ses chants dans l'assemblée", en: "Her songs in the assembly" },
        { fr: "Ses bonnes œuvres et ses aumônes", en: "Her good works and acts of mercy" },
        { fr: "Ses voyages missionnaires", en: "Her missionary journeys" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Dorcas « faisait beaucoup de bonnes œuvres et d'aumônes » (Actes 9:36) : elle cousait des vêtements pour les veuves. Le service discret laisse une trace profonde.",
        en: "Dorcas \"was full of good works and acts of mercy\" (Acts 9:36): she sewed garments for the widows. Quiet service leaves a deep mark.",
      },
    },
    {
      id: "q_barnabas", level: "E",
      prompt: { fr: "Que fit Barnabas dans Actes 4:36-37, et que signifie son surnom ?", en: "What did Barnabas do in Acts 4:36-37, and what does his nickname mean?" },
      choices: [
        { fr: "Il devint gouverneur de Chypre ; son surnom signifie « homme sage »", en: "He became governor of Cyprus; his nickname means \"wise man\"" },
        { fr: "Il vendit un champ et en apporta le prix aux apôtres ; son surnom signifie « fils d'exhortation »", en: "He sold a field and brought the money to the apostles; his nickname means \"son of encouragement\"" },
        { fr: "Il construisit la première salle de réunion ; son surnom signifie « bâtisseur »", en: "He built the first meeting hall; his nickname means \"builder\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "Joseph, surnommé par les apôtres Barnabas — « ce qui signifie fils d'exhortation » — « vendit un champ qu'il possédait, apporta l'argent, et le déposa aux pieds des apôtres » (Actes 4:36-37). Donner et encourager : deux services qui ouvrent la voie aux autres.",
        en: "Joseph, whom the apostles surnamed Barnabas — \"which is, being interpreted, Son of Encouragement\" — \"having a field, sold it and brought the money and laid it at the apostles' feet\" (Acts 4:36-37). Giving and encouraging: two services that open the way for others.",
      },
    },
    {
      id: "qD2", level: "D",
      prompt: { fr: "Selon Jacques 1:27, en quoi consiste la religion pure et sans tache ?", en: "According to James 1:27, what is pure and undefiled religion?" },
      choices: [
        { fr: "Multiplier les jeûnes publics", en: "Multiplying public fasts" },
        { fr: "Visiter les orphelins et les veuves dans leurs afflictions", en: "Visiting the orphans and widows in their affliction" },
        { fr: "Réciter de longues prières au marché", en: "Reciting long prayers in the marketplace" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« La religion pure et sans tache… consiste à visiter les orphelins et les veuves dans leurs afflictions, et à se préserver des souillures du monde » (Jacques 1:27). Porter l'eau à la veuve du village, c'est cette religion-là en actes.",
        en: "\"Pure religion and undefiled… is this: to visit the fatherless and widows in their affliction, and to keep oneself unstained by the world\" (James 1:27). Carrying water to the village widow is that religion in action.",
      },
    },
    {
      id: "qD3", level: "D",
      prompt: { fr: "Dans Matthieu 25:36, que dit Jésus au sujet du malade ?", en: "In Matthew 25:36, what does Jesus say about the sick?" },
      choices: [
        { fr: "« J'étais malade, et vous m'avez visité »", en: "\"I was sick, and you visited me\"" },
        { fr: "« J'étais malade, et vous avez prié de loin »", en: "\"I was sick, and you prayed from afar\"" },
        { fr: "« J'étais malade, et vous avez attendu ma guérison »", en: "\"I was sick, and you waited for my healing\"" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« J'étais malade, et vous m'avez visité » (Matthieu 25:36). Le panier de provisions déposé chez le malade rejoint ce que Jésus compte comme fait à lui-même.",
        en: "\"I was sick, and you visited me\" (Matthew 25:36). The basket of provisions left at the sick man's house counts, in Jesus' words, as done to himself.",
      },
    },
    {
      id: "qD4", level: "D",
      prompt: { fr: "Selon Actes 2:46, comment les premiers disciples prenaient-ils leur nourriture ensemble ?", en: "According to Acts 2:46, how did the first disciples take their food together?" },
      choices: [
        { fr: "En silence et à la hâte", en: "In silence and in haste" },
        { fr: "Chacun de son côté", en: "Each on his own" },
        { fr: "Avec joie et simplicité de cœur", en: "With gladness and singleness of heart" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ils rompaient le pain dans les maisons, et prenaient leur nourriture avec joie et simplicité de cœur » (Actes 2:46). La table du soir est une fête, pas une formalité.",
        en: "\"Breaking bread at home, they took their food with gladness and singleness of heart\" (Acts 2:46). The evening table is a feast, not a formality.",
      },
    },
    {
      id: "qD5", level: "D",
      prompt: { fr: "Quel est l'autre nom de Dorcas dans Actes 9:36 ?", en: "What is Dorcas's other name in Acts 9:36?" },
      choices: [
        { fr: "Lydie", en: "Lydia" },
        { fr: "Tabitha", en: "Tabitha" },
        { fr: "Priscille", en: "Priscilla" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il y avait à Joppé, parmi les disciples, une femme nommée Tabitha, ce qui signifie Dorcas » (Actes 9:36). Deux noms, une même réputation : ses bonnes œuvres et ses aumônes.",
        en: "\"Now there was at Joppa a certain disciple named Tabitha, which when translated means Dorcas\" (Acts 9:36). Two names, one reputation: her good works and acts of mercy.",
      },
    },
    {
      id: "qA2", level: "A",
      prompt: { fr: "Selon Galates 5:13, à quoi le disciple doit-il employer sa liberté ?", en: "According to Galatians 5:13, how should the disciple use his freedom?" },
      choices: [
        { fr: "À vivre selon la chair, puisqu'il est libre", en: "To live for the flesh, since he is free" },
        { fr: "À se retirer loin du village", en: "To withdraw far from the village" },
        { fr: "À se rendre, par la charité, serviteur des autres", en: "To become, through love, a servant of others" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ne faites pas de cette liberté un prétexte de vivre selon la chair ; mais rendez-vous, par la charité, serviteurs les uns des autres » (Galates 5:13). Libre POUR aimer, pas libre POUR soi.",
        en: "\"Don't use your freedom for gain to the flesh, but through love be servants to one another\" (Galatians 5:13). Free FOR loving, not free FOR self.",
      },
    },
    {
      id: "qA3", level: "A",
      prompt: { fr: "À la mort de Dorcas, que montrèrent les veuves en pleurant (Actes 9:39) ?", en: "At Dorcas's death, what did the weeping widows show (Acts 9:39)?" },
      choices: [
        { fr: "Les lettres qu'elle avait écrites", en: "The letters she had written" },
        { fr: "Les tuniques et les vêtements qu'elle faisait", en: "The coats and garments she made" },
        { fr: "Les provisions qu'elle avait mises de côté", en: "The provisions she had stored up" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Toutes les veuves l'entourèrent en pleurant, et lui montrèrent les tuniques et les vêtements que faisait Dorcas » (Actes 9:39). Un service discret, cousu point par point, était devenu la mémoire de toute une ville.",
        en: "\"All the widows stood by him weeping, and showing the coats and garments which Dorcas had made\" (Acts 9:39). A quiet service, sewn stitch by stitch, had become the memory of a whole town.",
      },
    },
    {
      id: "qA4", level: "A",
      prompt: { fr: "Selon Actes 2:47, qu'ajoutait le Seigneur chaque jour à l'Église ?", en: "According to Acts 2:47, what did the Lord add to the assembly day by day?" },
      choices: [
        { fr: "Ceux qui étaient sauvés", en: "Those who were being saved" },
        { fr: "De nouveaux bâtiments", en: "New buildings" },
        { fr: "Des règles supplémentaires", en: "Additional rules" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Et le Seigneur ajoutait chaque jour à l'Église ceux qui étaient sauvés » (Actes 2:47). Quand la communauté vit ses quatre persévérances, c'est Dieu lui-même qui fait grandir la famille.",
        en: "\"The Lord added to the assembly day by day those who were being saved\" (Acts 2:47). When the community lives its four steadfast practices, God himself grows the family.",
      },
    },
    {
      id: "qA5", level: "A",
      prompt: { fr: "Selon Actes 2:44-45, que faisaient les premiers croyants de leurs biens ?", en: "According to Acts 2:44-45, what did the first believers do with their possessions?" },
      choices: [
        { fr: "Ils les enterraient pour les protéger", en: "They buried them to protect them" },
        { fr: "Ils les vendaient et les partageaient selon les besoins de chacun", en: "They sold them and shared according to each one's need" },
        { fr: "Ils les gardaient chacun pour sa propre famille", en: "They each kept them for their own family" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Ils vendaient leurs propriétés et leurs biens, et ils en partageaient le produit entre tous, selon les besoins de chacun » (Actes 2:44-45). Le partage concret est la signature de la première communauté.",
        en: "\"They sold their possessions and goods, and distributed them to all, according as anyone had need\" (Acts 2:44-45). Concrete sharing is the signature of the first community.",
      },
    },
    {
      id: "qE2", level: "E",
      prompt: { fr: "D'où venait Barnabas, et de quelle tribu était-il (Actes 4:36) ?", en: "Where was Barnabas from, and of what tribe was he (Acts 4:36)?" },
      choices: [
        { fr: "C'était un Lévite, originaire de Chypre", en: "He was a Levite, a man of Cyprus by race" },
        { fr: "C'était un pêcheur de Galilée", en: "He was a fisherman from Galilee" },
        { fr: "C'était un centenier romain de Césarée", en: "He was a Roman centurion from Caesarea" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Joseph, surnommé par les apôtres Barnabas… Lévite, originaire de Chypre » (Actes 4:36). Un homme de l'île et du temple, devenu pont d'encouragement pour toute l'Église.",
        en: "\"Joseph, who by the apostles was also called Barnabas… a Levite, a man of Cyprus by race\" (Acts 4:36). A man of the island and the temple, become a bridge of encouragement for the whole Church.",
      },
    },
    {
      id: "qE3", level: "E",
      prompt: { fr: "Selon Matthieu 25:40, à qui le Roi assimile-t-il ce qui a été fait aux plus petits ?", en: "According to Matthew 25:40, with whom does the King identify what was done to the least?" },
      choices: [
        { fr: "Aux anges du ciel", en: "With the angels of heaven" },
        { fr: "Aux apôtres", en: "With the apostles" },
        { fr: "À lui-même : « c'est à moi que vous les avez faites »", en: "With himself: \"you did it to me\"" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Toutes les fois que vous avez fait ces choses à l'un de ces plus petits de mes frères, c'est à moi que vous les avez faites » (Matthieu 25:40). L'eau, le toit, le panier : chaque micro-service touche le Roi en personne.",
        en: "\"Because you did it to one of the least of these my brothers, you did it to me\" (Matthew 25:40). The water, the roof, the basket: every micro-service reaches the King himself.",
      },
    },
    {
      id: "qE4", level: "E",
      prompt: { fr: "Que fit Pierre dans la chambre haute de Joppé où reposait Dorcas (Actes 9:40) ?", en: "What did Peter do in the upper room at Joppa where Dorcas lay (Acts 9:40)?" },
      choices: [
        { fr: "Il fit sortir tout le monde, pria, et dit : « Tabitha, lève-toi » — et elle revint à la vie", en: "He put everyone out, prayed, and said, \"Tabitha, get up\" — and she returned to life" },
        { fr: "Il fit venir un médecin de Césarée", en: "He sent for a physician from Caesarea" },
        { fr: "Il organisa aussitôt ses funérailles", en: "He immediately arranged her funeral" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Pierre fit sortir tout le monde, se mit à genoux, et pria ; puis, se tournant vers le corps, il dit : Tabitha, lève-toi ! Elle ouvrit les yeux » (Actes 9:40). Dieu honore la servante discrète — et beaucoup crurent au Seigneur (Actes 9:42).",
        en: "\"Peter sent them all out, and knelt down and prayed. Turning to the body, he said, 'Tabitha, get up!' She opened her eyes\" (Acts 9:40). God honors the quiet servant — and many believed in the Lord (Acts 9:42).",
      },
    },
    {
      id: "qD6", level: "D",
      prompt: { fr: "Dans quelle ville vivait Dorcas, la disciple riche en bonnes œuvres (Actes 9:36) ?", en: "In which town did Dorcas, the disciple full of good works, live (Acts 9:36)?" },
      choices: [
        { fr: "À Césarée", en: "In Caesarea" },
        { fr: "À Joppé", en: "In Joppa" },
        { fr: "À Antioche", en: "In Antioch" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Il y avait à Joppé, parmi les disciples, une femme nommée Tabitha, ce qui signifie Dorcas » (Actes 9:36). Joppé, ville portuaire au bord de la mer, garde la mémoire de son service discret.",
        en: "\"Now there was at Joppa a certain disciple named Tabitha, which when translated means Dorcas\" (Acts 9:36). Joppa, a port town by the sea, keeps the memory of her quiet service.",
      },
    },
    {
      id: "qD7", level: "D",
      prompt: { fr: "Au cours du dernier repas, quel geste de serviteur Jésus laisse-t-il en exemple (Jean 13:14-15) ?", en: "During the last supper, what servant's gesture does Jesus leave as an example (John 13:14-15)?" },
      choices: [
        { fr: "Il lave les pieds de ses disciples", en: "He washes his disciples' feet" },
        { fr: "Il distribue des vêtements neufs", en: "He hands out new garments" },
        { fr: "Il attribue les places d'honneur", en: "He assigns the seats of honor" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Si donc je vous ai lavé les pieds, moi, le Seigneur et le Maître, vous devez aussi vous laver les pieds les uns aux autres ; car je vous ai donné un exemple » (Jean 13:14-15). Le Maître prend le tablier du serviteur : tout service du village découle de ce geste.",
        en: "\"If I then, the Lord and the Teacher, have washed your feet, you also ought to wash one another's feet. For I have given you an example\" (John 13:14-15). The Master takes the servant's apron: every village service flows from that gesture.",
      },
    },
    {
      id: "qD8", level: "D",
      prompt: { fr: "Selon Actes 2:46, où les premiers disciples se retrouvaient-ils assidus chaque jour, d'un commun accord ?", en: "According to Acts 2:46, where did the first disciples continue steadfastly every day, with one accord?" },
      choices: [
        { fr: "Sur la place du marché", en: "In the marketplace" },
        { fr: "Hors de la ville, au bord de la rivière", en: "Outside the city, by the riverside" },
        { fr: "Au temple — et ils rompaient le pain dans les maisons", en: "In the temple — and they broke bread at home" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Ils étaient chaque jour tous ensemble assidus au temple, ils rompaient le pain dans les maisons » (Actes 2:46). Deux lieux, une même vie : la grande assemblée et la table de la maison — comme le parvis et le village de cette scène.",
        en: "\"Day by day, continuing steadfastly with one accord in the temple, and breaking bread at home\" (Acts 2:46). Two places, one life: the great assembly and the home table — like this scene's courtyard and village.",
      },
    },
    {
      id: "qD9", level: "D",
      prompt: { fr: "Dans Matthieu 25:35, que dit le Roi au sujet de l'étranger ?", en: "In Matthew 25:35, what does the King say about the stranger?" },
      choices: [
        { fr: "« J'étais étranger, et vous m'avez indiqué la route »", en: "\"I was a stranger, and you showed me the road\"" },
        { fr: "« J'étais étranger, et vous m'avez recueilli »", en: "\"I was a stranger, and you took me in\"" },
        { fr: "« J'étais étranger, et vous avez fermé la porte »", en: "\"I was a stranger, and you shut the door\"" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« J'ai eu faim, et vous m'avez donné à manger ; j'ai eu soif, et vous m'avez donné à boire ; j'étais étranger, et vous m'avez recueilli » (Matthieu 25:35). Juste avant le malade visité (v.36), le Roi nomme l'affamé, l'assoiffé et l'étranger accueilli : l'hospitalité est un service qui le touche en personne.",
        en: "\"For I was hungry and you gave me food to eat. I was thirsty and you gave me drink. I was a stranger and you took me in\" (Matthew 25:35). Just before the sick who are visited (v.36), the King names the hungry, the thirsty and the welcomed stranger: hospitality is a service that reaches him personally.",
      },
    },
    {
      id: "qA6", level: "A",
      prompt: { fr: "Quand Saul, nouveau converti, est craint de tous à Jérusalem, que fait Barnabas (Actes 9:27) ?", en: "When Saul, newly converted, is feared by all in Jerusalem, what does Barnabas do (Acts 9:27)?" },
      choices: [
        { fr: "Il le prend avec lui, le conduit aux apôtres et raconte sa rencontre avec le Seigneur", en: "He takes him, brings him to the apostles and tells of his encounter with the Lord" },
        { fr: "Il lui conseille de retourner à Tarse sans se montrer", en: "He advises him to return to Tarsus without being seen" },
        { fr: "Il demande aux apôtres de le mettre à l'épreuve un an", en: "He asks the apostles to test him for a year" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« Alors Barnabas, l'ayant pris avec lui, le conduisit vers les apôtres, et leur raconta comment sur le chemin Saul avait vu le Seigneur » (Actes 9:27). Le fils de l'encouragement ouvre la porte à celui dont tous se méfient — encourager, c'est aussi se porter garant.",
        en: "\"But Barnabas took him and brought him to the apostles, and declared to them how he had seen the Lord on the way\" (Acts 9:27). The son of encouragement opens the door to the man everyone distrusts — encouraging also means vouching for someone.",
      },
    },
    {
      id: "qA7", level: "A",
      prompt: { fr: "Selon 1 Corinthiens 10:17, pourquoi les croyants, qui sont plusieurs, forment-ils un seul corps ?", en: "According to 1 Corinthians 10:17, why do the believers, who are many, form one body?" },
      choices: [
        { fr: "Parce qu'ils viennent tous du même village", en: "Because they all come from the same village" },
        { fr: "Parce qu'ils portent tous le même vêtement", en: "Because they all wear the same garment" },
        { fr: "Parce qu'il y a un seul pain, auquel tous participent", en: "Because there is one loaf of bread, of which all partake" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Puisqu'il y a un seul pain, nous qui sommes plusieurs, nous formons un seul corps ; car nous participons tous à un même pain » (1 Corinthiens 10:17). La Coupe et les pains de la Table de cette scène disent ce mystère : un seul pain rompu fait de plusieurs une seule famille.",
        en: "\"Because there is one loaf of bread, we, who are many, are one body; for we all partake of the one loaf of bread\" (1 Corinthians 10:17). The Cup and the loaves of this scene's Table tell that mystery: one broken loaf makes of many a single family.",
      },
    },
    {
      id: "qA8", level: "A",
      prompt: { fr: "Selon Hébreux 13:16, à quels sacrifices Dieu prend-il plaisir ?", en: "According to Hebrews 13:16, with what sacrifices is God well pleased?" },
      choices: [
        { fr: "Aux offrandes les plus coûteuses du temple", en: "The most costly offerings of the temple" },
        { fr: "À la bienfaisance et à la libéralité (faire le bien et partager)", en: "Doing good and sharing" },
        { fr: "Aux longues veilles solitaires", en: "Long solitary vigils" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Et n'oubliez pas la bienfaisance et la libéralité, car c'est à de tels sacrifices que Dieu prend plaisir » (Hébreux 13:16). Les cruches, les planches et les paniers du village sont des sacrifices agréables à Dieu — le service concret est un culte.",
        en: "\"But don't forget to be doing good and sharing, for with such sacrifices God is well pleased\" (Hebrews 13:16). The village's jugs, planks and baskets are sacrifices pleasing to God — concrete service is worship.",
      },
    },
    {
      id: "qE5", level: "E",
      prompt: { fr: "Comment Actes 4:32 décrit-il la multitude de ceux qui avaient cru ?", en: "How does Acts 4:32 describe the multitude of those who believed?" },
      choices: [
        { fr: "Un seul cœur et une seule âme — nul ne disait que ses biens lui appartenaient en propre", en: "One heart and soul — no one claimed that anything he possessed was his own" },
        { fr: "Douze assemblées dispersées dans la ville", en: "Twelve assemblies scattered through the city" },
        { fr: "Chacun gardait ses biens mais donnait la dîme", en: "Each kept his goods but gave a tithe" },
      ],
      answerIndex: 0,
      explain: {
        fr: "« La multitude de ceux qui avaient cru n'était qu'un cœur et qu'une âme. Nul ne disait que ses biens lui appartinssent en propre, mais tout était commun entre eux » (Actes 4:32). C'est dans ce climat que Barnabas vend son champ (Actes 4:36-37) : la communion des cœurs précède le partage des biens.",
        en: "\"The multitude of those who believed were of one heart and soul. Not one of them claimed that anything of the things which he possessed was his own, but they had all things in common\" (Acts 4:32). In that climate Barnabas sells his field (Acts 4:36-37): communion of hearts comes before sharing of goods.",
      },
    },
    {
      id: "qE6", level: "E",
      prompt: { fr: "Dans quelle ville, où Barnabas enseigna toute une année avec Saul, les disciples furent-ils appelés chrétiens pour la première fois (Actes 11:26) ?", en: "In which city, where Barnabas taught a whole year with Saul, were the disciples first called Christians (Acts 11:26)?" },
      choices: [
        { fr: "À Jérusalem", en: "In Jerusalem" },
        { fr: "À Éphèse", en: "In Ephesus" },
        { fr: "À Antioche", en: "In Antioch" },
      ],
      answerIndex: 2,
      explain: {
        fr: "« Pendant toute une année, ils se réunirent aux assemblées de l'Église, et ils enseignèrent beaucoup de personnes. Ce fut à Antioche que, pour la première fois, les disciples furent appelés chrétiens » (Actes 11:26). Barnabas était allé chercher Saul à Tarse (Actes 11:25) : l'encouragement porte du fruit jusque dans les noms.",
        en: "\"For a whole year they were gathered together with the assembly, and taught many people. The disciples were first called Christians in Antioch\" (Acts 11:26). Barnabas had gone to Tarsus to find Saul (Acts 11:25): encouragement bears fruit even in names.",
      },
    },
    {
      id: "qE7", level: "E",
      prompt: { fr: "Après le relèvement de Dorcas, chez qui Pierre demeura-t-il quelque temps à Joppé (Actes 9:43) ?", en: "After Dorcas was raised, with whom did Peter stay for a while in Joppa (Acts 9:43)?" },
      choices: [
        { fr: "Chez Corneille, le centenier de Césarée", en: "With Cornelius, the centurion of Caesarea" },
        { fr: "Chez Simon, un corroyeur", en: "With Simon, a tanner" },
        { fr: "Chez Lydie, la marchande de pourpre", en: "With Lydia, the seller of purple" },
      ],
      answerIndex: 1,
      explain: {
        fr: "« Pierre demeura quelque temps à Joppé, chez un corroyeur nommé Simon » (Actes 9:43). Un détail discret et une hospitalité humble — et c'est sur le toit de cette maison que Pierre recevra la vision qui ouvrira la porte aux nations (Actes 10:9-16).",
        en: "\"He stayed many days in Joppa with a tanner named Simon\" (Acts 9:43). A quiet detail and a humble hospitality — and on that house's roof Peter will receive the vision that opens the door to the nations (Acts 10:9-16).",
      },
    },
  ],
  tips: [
    { fr: "Fais d'abord sonner la Cloche du rassemblement, puis dresse la Table : les 6 pains nommés (accueil, pardon, joie, espérance, gratitude, unité) et la Coupe (Actes 2:42).", en: "First sound the Bell of gathering, then lay the Table: the 6 named loaves (welcome, forgiveness, joy, hope, gratitude, unity) and the Cup (Acts 2:42)." },
    { fr: "Trois micro-services attendent au village : porte 3 cruches d'eau du puits à la veuve, 3 planches de cèdre au toit du voisin, 3 paniers de provisions au malade.", en: "Three micro-services await in the village: carry 3 jugs of well water to the widow, 3 cedar planks to the neighbor's roof, 3 baskets of provisions to the sick man." },
    { fr: "Termine au Foyer de la prière commune et écoute Dorcas et Barnabas : « rendez-vous, par la charité, serviteurs les uns des autres » (Galates 5:13).", en: "Finish at the Hearth of common prayer and listen to Dorcas and Barnabas: \"through love be servants to one another\" (Galatians 5:13)." },
  ],
  lesson: {
    fr: "Ils persévéraient dans l'enseignement des apôtres, dans la communion fraternelle, dans la fraction du pain, et dans les prières. (Actes 2:42)",
    en: "They continued steadfastly in the apostles' teaching and fellowship, in the breaking of bread, and prayer. (Acts 2:42)",
  },
  audio: { ambience: "temple" },
  multiplayer: { mode: "coop", maxPlayers: 8 },
};

export default parcoursDuDiscipleScene4;
